/**
 * La bascule visio ↔ bulle de `<StudentLivePage>` (Wajih, daily du 21/10, `proposé` pour la forme) :
 * l'orbite d'abord, là où est le tandem, puis la visio qui sort de la bulle en grandissant, ou y rentre
 * en rétrécissant. La règle est pure (`stageMorph`) ; ici, elle tourne :
 *
 *  - `useStageMorph` suit ce que l'écran montre (la visio ou la bulle, et qui mène) : à chaque
 *    changement, il pose le plan, laisse l'orbite se jouer (`orbitDurationMs`, celle de `useTandemOrbit`), puis
 *    fait sortir ou rentrer la visio (`entree`, la courbe de la marque). Un changement en route repart
 *    de là où la bascule allait. En mouvement réduit (`enabled` faux), rien ne bouge ;
 *  - `<StageMorph>` découpe la visio dans un rond posé sur le visage de devant de la bulle, et la réduit
 *    pour que ce qu'elle montre au point visé (la vidéo du masteur, ou Sam devant) arrive sur lui ; à sa
 *    place, elle est entière, sans transformation. Elle garde sa taille tout du long : rien ne s'y remet
 *    en page. Sa structure ne change jamais (la visio n'est pas remontée en entrant ou en sortant).
 *
 * Tout passe par le pilote JS : une taille et une découpe ne passent pas par le pilote natif. Sur
 * Android, la vidéo du masteur (une `SurfaceView`) ne suit pas : la page montre sa photo le temps du
 * mouvement.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, type ViewStyle } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { orbitDurationMs } from "../../hooks/tandemOrbit"
import {
    STAGE_MORPH_STUCK_MARGIN_MS,
    stageMorphPlan,
    type StageMorphGeometry,
    type StageMorphStep,
    type TandemSight,
} from "./stageMorph"

/** Une bascule en cours : d'où, vers où, son plan, son temps, et l'avancée de la visio (0 bulle, 1 place). */
export type StageMorphRun = StageMorphStep & { from: TandemSight; to: TandemSight; progress: Animated.Value }

type MorphState = { settled: TandemSight; run: StageMorphRun | null }

const same = (a: TandemSight, b: TandemSight) => a.stage === b.stage && a.lead === b.lead

/**
 * `measuredFor` : la bascule dont la géométrie est connue (son avancée ; la bulle mesurée) ; la visio
 * n'avance qu'une fois la sienne connue.
 */
export function useStageMorph(
    target: TandemSight,
    enabled: boolean,
    measuredFor: Animated.Value | null,
    onStuck?: () => void
) {
    const { theme } = useUnistyles()
    // État d'interface : ce qui est posé au repos, et la bascule en cours. Relu pendant le rendu (l'état
    // dérivé de React) : la vue qui part ne disparaît pas une image avant que la bascule commence.
    const [state, setState] = useState<MorphState>({ settled: target, run: null })
    let current = state
    const goal = state.run?.to ?? state.settled
    if (!same(goal, target)) {
        const plan = stageMorphPlan(goal, target)
        current =
            enabled && plan.morph != null
                ? {
                      settled: goal,
                      run: {
                          plan,
                          phase: plan.orbitIn != null ? "orbit" : "morph",
                          from: goal,
                          to: target,
                          progress: new Animated.Value(plan.morph === "grow" ? 0 : 1),
                      },
                  }
                : { settled: target, run: null }
        setState(current)
    }
    const { run } = current

    // L'orbite d'abord : elle se joue d'elle-même là où est le tandem (`useTandemOrbit`), à sa durée.
    useEffect(() => {
        if (run == null || run.phase !== "orbit") return undefined
        const orbitMs = orbitDurationMs(run.plan.orbitIn ?? "bubble", run.to.lead === "sam", theme.motion.entree)
        const id = setTimeout(
            () => setState((now) => (now.run === run ? { ...now, run: { ...run, phase: "morph" } } : now)),
            orbitMs
        )
        return () => clearTimeout(id)
    }, [run, theme.motion.entree])

    // Puis la visio sort de la bulle, ou y rentre.
    const ready = run != null && measuredFor === run.progress
    useEffect(() => {
        if (run == null || run.phase !== "morph" || !ready) return undefined
        const move = Animated.timing(run.progress, {
            toValue: run.plan.morph === "grow" ? 1 : 0,
            duration: theme.motion.entree,
            easing: Easing.bezier(...theme.motion.easing),
            useNativeDriver: false,
        })
        move.start(({ finished }) => {
            if (!finished) return
            setState((now) => (now.run?.progress === run.progress ? { settled: run.to, run: null } : now))
        })
        return () => move.stop()
    }, [run, ready, theme.motion])

    // Une bascule qui ne finit pas (sa mesure, son animation) se pose d'office : la visio ne reste pas devant l'activité.
    const onStuckRef = useRef(onStuck)
    onStuckRef.current = onStuck
    useEffect(() => {
        if (run == null || run.phase !== "morph") return undefined
        const id = setTimeout(() => {
            onStuckRef.current?.()
            setState((now) => (now.run?.progress === run.progress ? { settled: run.to, run: null } : now))
        }, theme.motion.entree + STAGE_MORPH_STUCK_MARGIN_MS)
        return () => clearTimeout(id)
    }, [run, theme.motion.entree])

    return { settled: current.settled, run }
}

/** Les valeurs animées d'une géométrie, sur l'avancée de la visio. */
function useMorphStyles(geometry: StageMorphGeometry | null, progress: Animated.Value | null) {
    return useMemo(() => {
        if (geometry == null || progress == null) return null
        const along = (ends: readonly [number, number]) =>
            progress.interpolate<number>({ inputRange: [0, 1], outputRange: [ends[0], ends[1]] })
        return {
            outer: {
                left: along(geometry.left),
                top: along(geometry.top),
                width: along(geometry.width),
                height: along(geometry.height),
                borderRadius: along(geometry.radius),
            },
            inner: {
                left: along(geometry.innerLeft),
                top: along(geometry.innerTop),
                width: geometry.width[1],
                height: geometry.height[1],
                transform: [
                    { translateX: along(geometry.translateX) },
                    { translateY: along(geometry.translateY) },
                    { scale: along(geometry.scale) },
                ] as NonNullable<Animated.WithAnimatedObject<ViewStyle>["transform"]>,
            },
        }
    }, [geometry, progress])
}

/**
 * La visio, à sa place ou entre sa place et la bulle. `run` absent : à sa place. Pendant une bascule, sans
 * géométrie encore (la bulle pas mesurée) : entière si elle va rentrer, invisible si elle va sortir.
 */
export function StageMorph(props: {
    run: StageMorphRun | null
    geometry: StageMorphGeometry | null
    children: React.ReactNode
}) {
    const { run, geometry, children } = props
    const moving = run != null && run.phase === "morph" ? run : null
    const animated = useMorphStyles(moving != null ? geometry : null, moving?.progress ?? null)
    const waiting = moving != null && animated == null && moving.plan.morph === "grow"
    return (
        <Animated.View
            style={[styles.fill, animated != null && [styles.clip, animated.outer], waiting && styles.invisible]}
            pointerEvents={moving != null ? "none" : "box-none"}
        >
            <Animated.View style={[styles.fill, animated?.inner]} pointerEvents="box-none">
                {children}
            </Animated.View>
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    fill: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" },
    clip: { overflow: "hidden" },
    invisible: { opacity: 0 },
})
